/**
 * Utility functions for extracting embedded file metadata.
 *
 * Extracts creation and modification dates from embedded file metadata
 * (EXIF for images, PDF metadata via pdf-lib, core.xml for DOCX/XLSX).
 * Does NOT use File.lastModified which is the unreliable filesystem date.
 */
import exifr from "exifr";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";

/** Dates extracted from embedded file metadata */
export interface EmbeddedFileDates {
  /** Earliest embedded creation date (e.g. EXIF DateTimeOriginal, PDF CreationDate) */
  original_created_at: string | null;
  /** Embedded modification date (e.g. EXIF DateTime, PDF ModDate) */
  document_content_modified_date: string | null;
}

/**
 * Size threshold for client-side metadata extraction (50 MB).
 * Files larger than this skip client-side date extraction to avoid excessive
 * memory usage in the browser. The server-side pipeline extracts dates for
 * all files regardless, so large files will still get their dates populated.
 */
const CLIENT_EXTRACTION_SIZE_LIMIT = 50 * 1024 * 1024;

/**
 * Extract embedded creation and modification dates from a file's internal metadata.
 *
 * Supported formats:
 * - Images (JPEG, PNG, TIFF, HEIC): EXIF DateTimeOriginal / DateTime via exifr
 * - PDF: CreationDate / ModDate via pdf-lib
 * - DOCX/XLSX/XLSM: dcterms:created / dcterms:modified from docProps/core.xml
 *
 * Files exceeding 50 MB skip PDF/Office extraction to avoid browser memory
 * pressure during batch uploads. The server-side pipeline handles those.
 *
 * Returns null dates for unsupported formats or files above the size limit.
 */
export async function extractEmbeddedDates(file: File): Promise<EmbeddedFileDates> {
  const result: EmbeddedFileDates = {
    original_created_at: null,
    document_content_modified_date: null,
  };

  try {
    const ext = file.name.toLowerCase().split(".").pop() ?? "";
    const imageExts = ["jpg", "jpeg", "png", "tiff", "tif", "heic"];
    const officeExts = ["docx", "xlsx", "xlsm"];

    // EXIF extraction uses streaming reads via exifr, so no size limit needed
    if (imageExts.includes(ext)) {
      return await extractImageDates(file);
    }

    // PDF and Office extraction require reading the full file into memory.
    // Skip for large files to avoid browser memory pressure during batch uploads.
    if (file.size > CLIENT_EXTRACTION_SIZE_LIMIT) {
      return result;
    }

    if (ext === "pdf") {
      const buffer = await file.arrayBuffer();
      return await extractPdfDates(buffer);
    }

    if (officeExts.includes(ext)) {
      const buffer = await file.arrayBuffer();
      return await extractOfficeDates(buffer);
    }
  } catch (error) {
    console.warn("Failed to extract embedded dates from file:", error);
  }

  return result;
}

/** Convert an EXIF date value to ISO string, or null if not a valid Date. */
function toIsoOrNull(value: unknown): string | null {
  return value instanceof Date ? value.toISOString() : null;
}

/**
 * Extract dates from image EXIF metadata using exifr.
 * DateTimeOriginal = when photo was taken (creation).
 * DateTime = when image was last modified.
 */
async function extractImageDates(file: File): Promise<EmbeddedFileDates> {
  const exif = await exifr.parse(file, {
    pick: ["DateTimeOriginal", "DateTimeDigitized", "DateTime", "ModifyDate"],
  });

  if (!exif) return { original_created_at: null, document_content_modified_date: null };

  const creationDate = exif.DateTimeOriginal ?? exif.DateTimeDigitized ?? exif.DateTime;
  const modDate = exif.DateTime ?? exif.ModifyDate;

  return {
    original_created_at: toIsoOrNull(creationDate),
    document_content_modified_date: toIsoOrNull(modDate),
  };
}

/**
 * Extract dates from PDF metadata using pdf-lib.
 * Reads CreationDate and ModificationDate from the document info dictionary.
 */
async function extractPdfDates(buffer: ArrayBuffer): Promise<EmbeddedFileDates> {
  const result: EmbeddedFileDates = {
    original_created_at: null,
    document_content_modified_date: null,
  };

  const pdfDoc = await PDFDocument.load(buffer, { updateMetadata: false });

  const creationDate = pdfDoc.getCreationDate();
  if (creationDate) {
    result.original_created_at = creationDate.toISOString();
  }

  const modDate = pdfDoc.getModificationDate();
  if (modDate) {
    result.document_content_modified_date = modDate.toISOString();
  }

  return result;
}

/**
 * Extract dates from Office Open XML files (DOCX, XLSX, XLSM).
 * Reads docProps/core.xml for dcterms:created and dcterms:modified.
 */
async function extractOfficeDates(buffer: ArrayBuffer): Promise<EmbeddedFileDates> {
  const result: EmbeddedFileDates = {
    original_created_at: null,
    document_content_modified_date: null,
  };

  const zip = await JSZip.loadAsync(buffer);
  const coreXml = zip.file("docProps/core.xml");
  if (!coreXml) return result;

  const xmlText = await coreXml.async("text");

  // Extract dcterms:created
  const createdMatch = xmlText.match(/<dcterms:created[^>]*>([^<]+)<\/dcterms:created>/);
  if (createdMatch) {
    const date = new Date(createdMatch[1]);
    if (!isNaN(date.getTime())) result.original_created_at = date.toISOString();
  }

  // Extract dcterms:modified
  const modifiedMatch = xmlText.match(/<dcterms:modified[^>]*>([^<]+)<\/dcterms:modified>/);
  if (modifiedMatch) {
    const date = new Date(modifiedMatch[1]);
    if (!isNaN(date.getTime())) result.document_content_modified_date = date.toISOString();
  }

  return result;
}

/**
 * Extract comprehensive metadata from a file.
 * Dates are extracted from embedded metadata, NOT from File.lastModified.
 */
export async function extractFileMetadata(file: File) {
  const dates = await extractEmbeddedDates(file);

  return {
    original_created_at: dates.original_created_at,
    document_content_modified_date: dates.document_content_modified_date,
    file_size: file.size,
    content_type: file.type,
  };
}
